import { requireCompletedProfile } from "@hisani/auth";
import { approveInvoiceSchema, generateInvoiceDraftSchema, getInvoiceSchema, getSharedOwnerInvoiceSchema, listInvoicesSchema, listSharedOwnerInvoicesSchema, setAccountingAutoInvoiceEnabledSchema, shareInvoiceSchema, stableConflict, stableInvalidTransition, stableNotFound, } from "../validation.js";
import { requireFeatureMembership } from "./sealed-record-support.js";
const FEATURE = "ACCOUNTING";
function toOwnerInvoice(source, expectedOwnerHorsemanId) {
    const { snapshot } = source;
    if (snapshot.ownerHorsemanId !== expectedOwnerHorsemanId)
        return null;
    return {
        id: snapshot.invoiceId,
        stableName: snapshot.stableName,
        dateRangeStart: snapshot.dateRangeStart,
        dateRangeEnd: snapshot.dateRangeEnd,
        currency: snapshot.currency,
        lineItems: snapshot.lineItems.map((item) => ({
            horseName: item.horseName,
            categoryLabel: item.categoryLabel,
            vendor: item.vendor,
            billDate: item.billDate,
            description: item.description,
            amountMinor: item.amountMinor,
        })),
        totalMinor: snapshot.totalMinor,
        approvedAt: snapshot.approvedAt,
        sharedAt: source.sharedAt,
    };
}
/**
 * Resolves an `InvoiceGenerationTarget` into a concrete owner + horse set
 * ready to bill, or the well-defined "no Primary Owner linked yet" outcome
 * `docs/products/stable.md`/the Phase 7 task brief require (see
 * `domain/accounting/owner-link.ts`). Every horse has zero owner links
 * until Link An Owner ships, so `NO_PRIMARY_OWNER`/`NO_LINKED_HORSES` is
 * the expected outcome today — this is not an error path, it is the
 * correct behavior for a real, currently-empty data seam.
 */
async function resolveInvoiceTarget(stableId, target, dependencies) {
    if (target.kind === "HORSE") {
        const link = await dependencies.repository.getPrimaryOwnerLink(stableId, target.horseId);
        if (link === null)
            return { blocked: "NO_PRIMARY_OWNER" };
        return {
            ownerHorsemanId: link.ownerHorsemanId,
            horseId: target.horseId,
            horseIds: [target.horseId],
        };
    }
    const horses = await dependencies.repository.listHorsesForPrimaryOwner(stableId, target.ownerHorsemanId);
    if (horses.length === 0)
        return { blocked: "NO_LINKED_HORSES" };
    return {
        ownerHorsemanId: target.ownerHorsemanId,
        horseId: null,
        horseIds: horses.map((horse) => horse.horseId),
    };
}
/**
 * The core draft-building logic, shared by the interactive
 * {@link generateInvoiceDraft} use case and the system-driven monthly
 * auto-generate job (`accounting-auto-invoice-scan-use-case.ts`) — resolves
 * the target, rolls up every still-billable (bill, horse) share into line
 * items, and upserts the draft. Callers are responsible for authorization;
 * this function does none (the scan job has no `SessionPrincipal` to check).
 */
export async function buildInvoiceDraft(stableId, target, dateRangeStart, dateRangeEnd, currency, generatedByHorsemanId, dependencies) {
    const resolved = await resolveInvoiceTarget(stableId, target, dependencies);
    if ("blocked" in resolved)
        return { status: resolved.blocked };
    const [shares, policy] = await Promise.all([
        dependencies.repository.listBillableAllocationsForInvoice({
            stableId,
            horseIds: resolved.horseIds,
            dateRangeStart,
            dateRangeEnd,
            currency,
        }),
        dependencies.policy.get(),
    ]);
    const horseNameById = new Map(resolved.horseId === null
        ? (await dependencies.repository.listHorsesForPrimaryOwner(stableId, resolved.ownerHorsemanId)).map((horse) => [horse.horseId, horse.horseName])
        : [
            [
                resolved.horseId,
                (await dependencies.repository.getHorse(stableId, resolved.horseId))
                    ?.name ?? "",
            ],
        ]);
    const lineItems = shares.map((share) => ({
        billId: share.billId,
        horseId: share.horseId,
        horseName: horseNameById.get(share.horseId) ?? "",
        categoryCode: share.categoryCode,
        categoryLabel: policy.expenseCategories[share.categoryCode]?.label ??
            share.categoryCode,
        vendor: share.vendor,
        billDate: share.billDate,
        description: share.description,
        amountMinor: share.amountMinor,
    }));
    const now = dependencies.clock.now();
    const invoice = await dependencies.repository.upsertInvoiceDraft({
        id: dependencies.newId(),
        stableId,
        horseId: resolved.horseId,
        ownerHorsemanId: resolved.ownerHorsemanId,
        dateRangeStart,
        dateRangeEnd,
        currency,
        lineItems,
        generatedByHorsemanId,
        now,
    });
    return { status: "DRAFT", invoice };
}
/**
 * Generates (or, while still `DRAFT`, regenerates) an invoice draft for a
 * horse or an owner's linked horses over a date range
 * (`docs/products/stable.md`: "The Trainer generates an invoice on demand
 * by picking a horse/owner and date range, producing a reviewable draft").
 * Never auto-approves — the caller always gets back a `DRAFT` to review,
 * even one with zero line items (a horse/owner with no billable expenses in
 * range is a legitimate, empty result, not an error).
 */
export async function generateInvoiceDraft(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = generateInvoiceDraftSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    return buildInvoiceDraft(parsed.stableId, parsed.target, parsed.dateRangeStart, parsed.dateRangeEnd, parsed.currency, actor.account.id, dependencies);
}
export async function getInvoice(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = getInvoiceSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "VIEW");
    return dependencies.repository.getInvoice(parsed.stableId, parsed.invoiceId);
}
export async function listInvoices(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = listInvoicesSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "VIEW");
    return dependencies.repository.listInvoices({
        stableId: parsed.stableId,
        ...(parsed.status === undefined ? {} : { status: parsed.status }),
        limit: parsed.limit,
        cursor: parsed.cursor,
    });
}
/** Lists only invoices deliberately disclosed to the authenticated invoiced
 * Owner. This is intentionally independent of Stable membership: ownership
 * is proven by the immutable snapshot, while the repository query is scoped
 * to the authenticated Horseman before any record reaches this mapper. */
export async function listSharedOwnerInvoices(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = listSharedOwnerInvoicesSchema.parse(input);
    const page = await dependencies.repository.listSharedOwnerInvoiceSnapshots({
        ownerHorsemanId: actor.account.id,
        limit: parsed.limit,
        cursor: parsed.cursor,
    });
    return {
        records: page.records.flatMap((source) => {
            const invoice = toOwnerInvoice(source, actor.account.id);
            return invoice === null ? [] : [invoice];
        }),
        nextCursor: page.nextCursor,
    };
}
/** Returns an Owner-safe invoice projection or `null` for every
 * non-disclosable condition, including another Owner's invoice. */
export async function getSharedOwnerInvoice(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = getSharedOwnerInvoiceSchema.parse(input);
    const source = await dependencies.repository.getSharedOwnerInvoiceSnapshot(actor.account.id, parsed.invoiceId);
    return source === null ? null : toOwnerInvoice(source, actor.account.id);
}
/**
 * Approves a draft, freezing it into an immutable snapshot
 * (`docs/products/stable.md`: "Approving an invoice creates an immutable
 * snapshot ... Later expense or Primary Owner changes never rewrite an
 * approved snapshot"). The repository atomically claims every (bill,
 * horse) share against `stable_bill_invoice_claims` so the same expense
 * can never be double-billed across two approved invoices, and rejects an
 * empty draft or a version conflict.
 */
export async function approveInvoice(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = approveInvoiceSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    const dashboard = await dependencies.repository.getStableDashboard(parsed.stableId, actor.account.id);
    if (dashboard === null)
        throw stableNotFound();
    const existing = await dependencies.repository.getInvoice(parsed.stableId, parsed.invoiceId);
    if (existing === null)
        throw stableNotFound();
    if (existing.status === "APPROVED") {
        throw stableInvalidTransition("This invoice has already been approved.");
    }
    if (existing.lineItems.length === 0) {
        throw stableInvalidTransition("An invoice with no line items cannot be approved.");
    }
    const now = dependencies.clock.now();
    const invoice = await dependencies.repository.approveInvoice({
        stableId: parsed.stableId,
        invoiceId: parsed.invoiceId,
        expectedVersion: parsed.expectedVersion,
        stableName: dashboard.stable.name,
        actorId: actor.account.id,
        now,
    });
    if (invoice === null)
        throw stableConflict();
    return invoice;
}
/** Shares an approved invoice with its Owner, notifying them via the
 * existing Stable outbox (`docs/products/stable.md`: "sharing an invoice
 * notifies the Owner"). Sharing an unapproved draft is refused — Owners
 * never see bookkeeping that has not gone through Trainer approval. */
export async function shareInvoice(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = shareInvoiceSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    const existing = await dependencies.repository.getInvoice(parsed.stableId, parsed.invoiceId);
    if (existing === null)
        throw stableNotFound();
    if (existing.status !== "APPROVED") {
        throw stableInvalidTransition("Only an approved invoice can be shared with its Owner.");
    }
    const now = dependencies.clock.now();
    const invoice = await dependencies.repository.shareInvoice({
        stableId: parsed.stableId,
        invoiceId: parsed.invoiceId,
        actorId: actor.account.id,
        now,
    });
    if (invoice === null)
        throw stableConflict();
    return invoice;
}
/** Toggles the fixed-monthly auto-generate setting
 * (`docs/products/stable.md`: "opts a Stable into a fixed-monthly
 * auto-generate toggle that produces drafts for review instead of
 * auto-approving them"). The toggle only changes whether a scheduled job
 * *would* call `generateInvoiceDraft` on the Stable's behalf — no such
 * schedule is wired up by this phase (see the Phase 7 completion report);
 * this use case only records the setting correctly. */
export async function setAccountingAutoInvoiceEnabled(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = setAccountingAutoInvoiceEnabledSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    await dependencies.repository.setAccountingAutoInvoiceEnabled({
        stableId: parsed.stableId,
        enabled: parsed.enabled,
        now: dependencies.clock.now(),
    });
}
